/**
 * Display text for TipTap @mention nodes.
 *
 * A mention is shown as the mentioned league's HANDLE ("@bookclub"), never
 * its display name: names collide ("Chris", "Hannah"), handles don't, so the
 * reader can always tell who was tagged. The handle lives in
 * `attrs.subdomain`; `attrs.label` is what the composer displayed at insert
 * time, which older content and older app builds set to the league's name.
 * Renderers must therefore read through these helpers rather than `label`,
 * so that content predating the switch also renders as a handle.
 *
 * Pure and dependency-free on purpose: it runs in the browser (editors,
 * static renderers), on the server (emails, RSS, comment sanitizing), and is
 * mirrored on mobile. Keep in sync with mobile/lib/util/mentionText.ts.
 */

export interface MentionNodeAttrs {
  id?: unknown;
  label?: unknown;
  subdomain?: unknown;
}

// The subdomain charset enforced at league creation
// (RecLeague.validateSubdomain). Anything else is not a handle we can link.
const HANDLE_REGEX = /^[a-z0-9_]+$/;

/**
 * The mention's handle (lowercased, trimmed), or null when the node carries
 * no usable subdomain. Null means "render unlinked" — never a crash.
 */
export function getMentionHandle(
  attrs: MentionNodeAttrs | null | undefined,
): string | null {
  const subdomain = attrs?.subdomain;
  if (typeof subdomain !== "string") return null;
  const handle = subdomain.trim().toLowerCase();
  return HANDLE_REGEX.test(handle) ? handle : null;
}

/**
 * The text a mention renders as: "@<handle>". Falls back to the stored label
 * and then the id only for malformed nodes with no handle, so a reader still
 * sees that something was mentioned.
 */
export function getMentionDisplayText(
  attrs: MentionNodeAttrs | null | undefined,
): string {
  const handle = getMentionHandle(attrs);
  if (handle) return `@${handle}`;
  const label = attrs?.label;
  if (typeof label === "string" && label.trim()) return `@${label.trim()}`;
  const id = attrs?.id;
  return `@${typeof id === "string" || typeof id === "number" ? id : ""}`;
}

/**
 * The attrs a composer should store when inserting a mention of a league.
 * `label` is the handle too: TipTap's own `renderText`/`renderHTML` and any
 * client that predates the handle switch read `label`, so writing the handle
 * there keeps their display consistent with ours.
 */
export function buildMentionAttrs(recLeague: {
  id: number | string;
  subdomain: string;
}): { id: string; label: string; subdomain: string } {
  const handle = recLeague.subdomain.trim().toLowerCase();
  return { id: String(recLeague.id), label: handle, subdomain: handle };
}
